/**
 * Действия для корзины:
 *
 * 1. Добавление товара в корзину [п 2.6]:
 *    1.1   productClick:  productClickSuccess();
 *    1.2   addToCart:     updateItemCountSuccess();
 *
 * 2. Удаление товара из корзины [п. 2.7]:
 *    2.1   removeFromCart:
 *        2.1.1   updateItemCountSuccess()    - Уменьшение count продукта в корзине ("-");
 *        2.1.2   removeItemSuccess()         - Удаление продукта из корзины ("х");
 *        2.1.3   clearCheckoutSuccess()      - Очистка корзины ("очистить");
 *
 */

import { difference } from '../../../../utils/helpers';
import { convertCartProductToAnalytic } from '../../helpers';

// Router
import { getRoute } from '../../../../entry/routes';

/**
 * 1.1
 * Ивент для нажатия на товар
 * (используется для добавления в корзину)
 *
 * @param {object} action
 * @param {object} prevState
 * @param {object} nextState
 *
 * @returns {object} outItem Ивент
 */
export function productClickSuccess(action, prevState, nextState) {
  // check if ITEMS_UPDATE call from constructor
  // if not - returns null

  if (
    action.type === '@cart/ITEMS_UPDATE' &&
    !prevState.location.current.pathname.includes('/constructor')
  ) {
    return null;
  }

  // Continue after check
  const { composition } = difference(nextState.cart, prevState.cart);
  const diffKey = Object.keys(composition)[0];

  // Get clicked product
  const clickedProductInCart = nextState.cart.composition[diffKey] || null;
  if (!clickedProductInCart) {
    console.error(
      `[analytic event] Clicked product not found in CART with this diffKey -> ${diffKey}`,
    );
    return null;
  }

  // Get previous state of clicked product && check if count is decreased
  const previousProductState = prevState.cart.composition[diffKey] || null;
  if (previousProductState && previousProductState.count > clickedProductInCart.count) {
    return null;
  }

  const processedProduct = convertCartProductToAnalytic(clickedProductInCart);

  const outItem = productClickHandler(processedProduct);

  return outItem;
}

// отправляем эвент, когда положили товар в корзину из апсейла
export function addToCartFromUpsale(action) {
  const { variation, index } = action.payload;

  const product = convertCartProductToAnalytic({ item: variation });

  if (product) {
    delete product.count;

    const products = [{ ...product }]; // Продукт, на который кликнули

    const outItem = {
      event: 'upsale-checkout-web',
      eventCategory: 'Ecommerce',
      eventAction: 'upsale-checkout-web',
      eventLabel: `${index + 1}`,
      ecommerce: {
        click: {
          actionField: { list: product.category },
          products: [...products],
        },
      },
    };

    return outItem;
  }

  return null;
}

/**
 * 1.2 | 2.1.1
 * Ивент для добавления товара в корзину.
 * Также для удаления товара по значку "-" в корзине
 *
 * @param {object} action
 * @param {object} prevState
 * @param {object} nextState
 *
 * @returns {object} outItem Ивент
 */
export function updateItemCountSuccess(action, prevState, nextState) {
  // check if ITEMS_UPDATE call from constructor
  // if not - returns null

  if (
    action.type === '@cart/ITEMS_UPDATE' &&
    !prevState.location.current.pathname.includes('/constructor')
  ) {
    return null;
  }

  // Continue after check
  const { composition } = difference(nextState.cart, prevState.cart);
  const diffKey = Object.keys(composition)[0];

  // Get clicked product
  const clickedProductInCart = nextState.cart.composition[diffKey] || null;
  if (!clickedProductInCart) {
    console.error(
      `[analytic event] Clicked product not found in CART with this diffKey -> ${diffKey}`,
    );
    return null;
  }

  const processedProduct = convertCartProductToAnalytic(clickedProductInCart);

  // Get previous state of clicked product && check if count is decreased
  const previousProductState = prevState.cart.composition[diffKey] || null;
  if (previousProductState && previousProductState.count > clickedProductInCart.count) {
    return removeFromCartHandler(processedProduct);
  }

  const currency = nextState.city.userCity.currency.unit || '';

  const outItem = addToCartHandler(processedProduct, currency);

  return outItem;
}

/**
 * 2.1.2
 * Ивент для удаления товара из корзины
 *
 * @param {object} action
 * @param {object} prevState
 * @param {object} nextState
 *
 * @returns {object} outItem Ивент для удаления товара из корзины
 */
export function removeItemSuccess(action, prevState, nextState) {
  const { composition } = difference(prevState.cart, nextState.cart);
  const diffKey = Object.keys(composition)[0];

  // Get clicked product
  const removedProductFromCart = prevState.cart.composition[diffKey] || null;
  if (!removedProductFromCart) {
    console.error(
      `[analytic event] Removed product not found in CART with this diffKey -> ${diffKey}`,
    );
    return null;
  }

  const processedProduct = convertCartProductToAnalytic(removedProductFromCart);

  const outItem = removeFromCartHandler(processedProduct);

  return outItem;
}

/**
 * 2.1.3
 * Ивент для очистки корзины
 *
 * @param {object} action
 * @param {object} prevState
 * @param {object} nextState
 *
 * @returns {object} outItem Ивент очистки корзины
 */
export function clearCheckoutSuccess(action, prevState, nextState) {
  const { composition } = difference(prevState.cart, nextState.cart);

  if (!composition || !Array.isArray(composition) || composition.length === 0) {
    console.error(`[analytic event] Composition error: `, composition);
    return null;
  }

  const processedProducts = [];
  composition.forEach(item => {
    const processedItem = convertCartProductToAnalytic(item);
    processedProducts.push(processedItem);
  });

  const outItem = removeFromCartHandler(processedProducts);

  return outItem;
}

/* ===== HANDLERS ===== */
/**
 * Создает ивент "productClick" для отправки в dataLayer
 * (используется только перед addToCart)
 *
 * @param {object} product Товар со структурой для аналитики
 * @returns {object} outItem Ивент нажатия на товар для dataLayer
 */
function productClickHandler(product) {
  if (product) {
    product.position = product.count;
    delete product.count;

    const products = [{ ...product }]; // Продукт, на который кликнули
    const actionField = { list: product.category };

    const outItem = {
      event: 'productClick',
      eventCategory: 'Ecommerce',
      eventAction: 'productClick',
      eventLabel: undefined,

      ecommerce: {
        click: {
          actionField,
          products: [...products],
        },
      },
    };

    return outItem;
  }

  return null;
}

/**
 * Создает ивент "addToCart" для отправки в dataLayer
 *
 * @param {object} product Товар со структурой для аналитики
 * @param {string} currency Код валюты
 * @returns {object} outItem Ивент добавления товара в корзину для dataLayer
 */
function addToCartHandler(product, currency) {
  if (product) {
    product.position = product.count;
    delete product.count;

    const products = [{ ...product }]; // Продукт, который добавили в корзину

    const outItem = {
      event: 'addToCart',
      eventCategory: 'Ecommerce',
      eventAction: 'addToCart',
      eventLabel: undefined,

      ecommerce: {
        add: {
          currencyCode: currency.toUpperCase(),
          products: [...products],
        },
      },
    };

    return outItem;
  }

  return null;
}

/**
 * Создает ивент "removeItem" для отправки в dataLayer
 *
 * @param {object|array} product Товар со структурой для аналитики
 * @returns {object} outItem Ивент удаления для dataLayer
 */
function removeFromCartHandler(product) {
  if (product) {
    delete product.count;

    const products = Array.isArray(product) ? [...product] : [{ ...product }]; // Продукт, который удалили из корзины

    const outItem = {
      event: 'removeFromCart',
      eventCategory: 'Ecommerce',
      eventAction: 'removeFromCart',
      eventLabel: undefined,

      ecommerce: {
        remove: {
          products: [...products],
        },
      },
    };

    return outItem;
  }

  return null;
}
